import React from "react";
import { BellRing, CreditCard, Plus, ReceiptText, Store } from "lucide-react";

import ParticipantesMesa from "./ParticipantesMesa";
import ResumoPagoComanda from "./ResumoPagoComanda";
import RodadaComanda from "./RodadaComanda";

const dinheiro = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;
// Rodadas do garçom compartilham o pedido da mesa: o pedido_id sozinho repete
const chaveRodada = (r, i) => `${r.pedido_id || "mesa"}-${r.cliente_id || "sem-dono"}-${r.criado_em || ""}-${i}`;

function Secao({ titulo, children }) {
  return (
    <section className="space-y-2.5">
      <p className="text-[11px] font-bold uppercase tracking-widest opacity-60 px-1">{titulo}</p>
      {children}
    </section>
  );
}

// A comanda da mesa: tudo que está na mesa, com o nome de quem pediu cada
// rodada. É conta única (modelo escolhido pelo dono), então todos veem tudo —
// por isso só o primeiro nome aparece, nunca CPF ou telefone.
export default function ComandaMesa({
  mesa, comanda, cliente, config, onAdicionar, onChamarGarcom, onPagar,
  chamadoAberto, darkMode, participantes, fotoDe, onDecidirEntrada, linkConvite, onComprovante,
}) {
  const rodadas = comanda?.rodadas || [];
  // O que ESTA conta mandou vem sempre primeiro, separado do resto da mesa
  // (pedido do dono, 23/09). Dentro de cada bloco, na ordem em que saiu.
  const minhas = rodadas.filter((r) => r.meu);
  const daMesa = rodadas.filter((r) => !r.meu);
  const fundo = darkMode ? "bg-zinc-950 text-zinc-100" : "bg-white text-zinc-900";

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col ${fundo}`} data-e2e="mesa-comanda">
      <div className="bg-primary text-primary-foreground px-4 pt-6 pb-5">
        <div className="flex items-start gap-3">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-widest opacity-80">Comanda da</p>
            <h2 className="text-3xl font-black truncate">{(mesa?.nome || `Mesa ${mesa?.numero ?? ""}`).toUpperCase()}</h2>
          </div>
          <div className="ml-auto pt-1">
            <ParticipantesMesa
              dados={participantes} euId={cliente?.id} fotoDe={fotoDe} darkMode={darkMode}
              onDecidir={onDecidirEntrada} linkConvite={linkConvite} mesa={mesa}
            />
          </div>
        </div>
        <div className="mt-3 flex items-end justify-between">
          <span className="text-sm opacity-90">
            {rodadas.length} {rodadas.length === 1 ? "pedido" : "pedidos"} na mesa
          </span>
          <span className="text-3xl font-black" data-e2e="mesa-total">{dinheiro(comanda?.total)}</span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {rodadas.length === 0 ? (
          <p className="text-center text-sm opacity-60 py-10">
            Nada pedido ainda. Toque em "Adicionar itens" para começar.
          </p>
        ) : null}
        {minhas.length ? (
          <Secao titulo="Seus pedidos">
            {minhas.map((r, i) => <RodadaComanda key={chaveRodada(r, i)} rodada={r} fotoDe={fotoDe} darkMode={darkMode} />)}
          </Secao>
        ) : null}
        {daMesa.length ? (
          <Secao titulo={minhas.length ? "Pedidos da mesa" : "Pedidos"}>
            {daMesa.map((r, i) => <RodadaComanda key={chaveRodada(r, i)} rodada={r} fotoDe={fotoDe} darkMode={darkMode} />)}
          </Secao>
        ) : null}
        <ResumoPagoComanda comanda={comanda} darkMode={darkMode} />
        {comanda?.meu_total > 0 ? (
          <p className="text-center text-xs opacity-60" data-e2e="mesa-meu-resumo">
            {comanda.meu_restante > 0
              ? `Seus itens: ${dinheiro(comanda.meu_total)} · falta pagar ${dinheiro(comanda.meu_restante)}`
              : `Seus itens: ${dinheiro(comanda.meu_total)} · tudo pago ✓`}
          </p>
        ) : null}
        {/* Quem pagou por outros (ou teve a parte paga) também tem comprovante,
            mesmo sem produto próprio. Só com o pagamento na mesa ligado: o
            comprovante é o QR de saída */}
        {onComprovante && config?.pagamento_online && ((comanda?.meu_total > 0 && comanda.meu_restante < comanda.meu_total) || comanda?.ja_pago > 0) ? (
          <button type="button" onClick={onComprovante} data-e2e="mesa-meu-comprovante"
            className="mx-auto flex items-center gap-1.5 text-sm font-bold text-primary">
            <ReceiptText className="w-4 h-4" /> Meu comprovante
          </button>
        ) : null}
      </div>

      <div className={`px-4 pt-3 pb-6 space-y-2 border-t ${darkMode ? "border-zinc-800" : "border-zinc-200"}`}>
        <button
          type="button"
          onClick={onAdicionar}
          data-e2e="mesa-adicionar"
          className="w-full h-14 rounded-full bg-primary text-primary-foreground text-base font-black flex items-center justify-center gap-2 active:scale-[0.98] transition"
        >
          <Plus className="w-5 h-5" /> ADICIONAR ITENS
        </button>
        <div className="flex gap-2">
          {config?.chamar_garcom ? (
            <button
              type="button"
              onClick={onChamarGarcom}
              disabled={chamadoAberto}
              data-e2e="mesa-garcom"
              className={`flex-1 h-12 rounded-full border-2 text-sm font-bold flex items-center justify-center gap-1.5 transition ${
                chamadoAberto
                  ? "border-emerald-500/40 text-emerald-500"
                  : "border-primary/40 text-primary active:scale-[0.98]"
              }`}
            >
              <BellRing className="w-4 h-4" />
              {chamadoAberto ? "Garçom a caminho" : "Chamar garçom"}
            </button>
          ) : null}
          {config?.pagamento_online ? (
            <button
              type="button"
              onClick={onPagar}
              disabled={!(comanda?.restante > 0)}
              data-e2e="mesa-pagar"
              className="flex-1 h-12 rounded-full border-2 border-primary/40 text-primary text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.98] transition disabled:opacity-50"
            >
              <CreditCard className="w-4 h-4" /> Pagar
            </button>
          ) : (
            <div className="flex-1 h-12 rounded-full border-2 border-dashed border-zinc-400/40 text-xs font-bold opacity-70 flex items-center justify-center gap-1.5 text-center px-2">
              <Store className="w-4 h-4 shrink-0" /> Vá até o caixa para pagar
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
